import {
  Children,
  ChangeEvent,
  cloneElement,
  isValidElement,
  ReactElement,
  ReactNode,
} from "react";

import Box from "../Box/Box";
import { BoxProps } from "../Box/box.type";
import RadioButton from "./RadioButton";

export interface RadioButtonGroupProps extends BoxProps {
  /**
   Everything enclosed in RadioButtonGroup tags.
   */
  children: ReactNode;
  /**
   Handler function for radio button selection. Needs to be implemented separately when using this component.
   */
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  /**
   A semantic designator for the group of radio buttons. This value will be used for aria-label attribute internally.
   */
  name: string;
  /**
   Value of the currently selected radio button.
   */
  value: string;
  /**
   *The element's unique identifier
   */
  id?: string;
  /**
   Specify to display a vertical list or a horizontal one.
   */
  orientation?: "vertical" | "horizontal";
  /**
   Custom css to be applied.
   */
  css?: string;
}

export default function RadioButtonGroup({
  children,
  onChange,
  name,
  value,
  orientation = "vertical",
  css,
  ...props
}: RadioButtonGroupProps) {
  const flexDirection = orientation === "horizontal" ? "row" : "column";

  return (
    <Box
      aria-label={name}
      css={css}
      role="radiogroup"
      display="flex"
      flexDirection={flexDirection}
      {...props}
    >
      {children &&
        Children.toArray(children).map((child) => {
          if (isValidElement(child) && child.type === RadioButton) {
            const isSelected = value === child.props.value;
            return cloneElement(child as ReactElement, {
              onChange: onChange,
              name: name,
              isSelected: isSelected,
            });
          }
          return child;
        })}
    </Box>
  );
}
